<template>
  <div>
    <div class="mt-6 flex justify-between items-center">
      <p class="send-to-patient">{{ $t("OrderPage[\"Send Order Notification to Patient\"]") }}</p>
      <el-button 
        v-if="isShowScheduleSendDateBtn"
        class="schedule-date-btn pl-3 pt-1.5" 
        @click="isOpenScheduleSendDateDialog = true"
      >
        <div class="flex items-center">
          <span class="material-icons-outlined calendar-icon mr-2">calendar_today</span>
          <p>{{ $t("OrderPage[\"Schedule send date\"]") }}</p>
        </div>
      </el-button>
      <ScheduleSendDateDialog 
        :is-open-dialog="isOpenScheduleSendDateDialog"
        :name="patientFullName"
        @close="isOpenScheduleSendDateDialog = false"
      />
    </div>
    <p v-if="!isSendDateEmpty" class="send-to-patient-text ml-2 mt-1">
      {{ $t("OrderPage[\"Send this order to patient on\"]") }}
      <span class="date-and-time">{{ sendDate.date }}</span><span v-if="sendDate.time"> at </span>
      <span v-if="sendDate.time" class="date-and-time"> {{ sendDate.time }}</span>
    </p>
    <div class="patient-contact-container pt-3 pb-4 px-3 mt-3">
      <SendNotificationInput 
        :label="$t('OrderPage[\'Send email to\']')"
        :is-send="isSendEmail"
        :input-value="email"
        :disabled="isEmailDisabled"
        prefix="email"
        @click="updateIsSendNotification('email', $event)"
        @blur="updateEmailPhone('email', $event)"
        @enterPressed="updateEmailPhone('email', $event)"
      />
      <hr class="email-text-divider mt-4">
      <SendNotificationInput
        class="mt-3"
        :label="$t('OrderPage[\'Send Text message to\']')"
        :is-send="isSendText"
        :input-value="phone"
        :disabled="isPhoneDisabled"
        prefix="call"
        @input="updateEmailPhone('phone', $event)"
        @click="updateIsSendNotification('text', $event)"
        @enterPressed="updateEmailPhone('text', $event)"
      />
    </div>
  </div>
</template>

<script>
import ScheduleSendDateDialog from "./components/ScheduleSendDateDialog.vue";
import SendNotificationInput from "./components/SendNotificationInput.vue";
import { mapGetters, mapState } from 'vuex';
import _ from "lodash";
import { formatPhoneNumber } from "@/utils/va-order-test/checkPhoneInput.js";
import { notify } from '@/utils/notify';

export default {
  components: {
    ScheduleSendDateDialog,
    SendNotificationInput
  },  
  props: {
    isSendEmail: {
      type: Boolean,
      default: false,
    },
    isSendText: {
      type: Boolean,
      default: false,
    },
    email: {
      type: String,
      default: ""
    },
    phone: {
      type: String,
      default: "",
    },
    defaultSettings:{
      type: Object,
      default: () => {},
    },
    orderDraft: {
      type: Object,
      default: () => {},
    },
  },

  data() {
    return {
      isOpenScheduleSendDateDialog: false,
      patient: JSON.parse(sessionStorage.getItem("patient")),   
      isShowScheduleSendDateBtn: false,
    }
  },

  computed: {
    ...mapState("orderTest", ["isPatientInfoInSessionStorage", "isBackFromCheckoutPage", "patientAreaCodeChangeCount"]),
    ...mapState("orderTest/order", ["sendDate"]),
    // Family Bundle - State (UI hidden, keeping for potential future use)
    ...mapState("orderTest/familyDiscount", ["familyDiscountOrders"]),
    ...mapGetters("orderTest/familyDiscount", ["familyDiscountEligible"]),

    patientFullName() {
      if (!this.patient) {
        return "";
      }
      let nameArray = [this.patient.firstName, 
        this.patient.middleName, 
        this.patient.lastName];
      return nameArray.join(" ");
    },

    isSendDateEmpty() {
      return this.sendDate.date === "";
    },
    isEmailDisabled() {
      const val = (this.email || "").trim();
      return val.length === 0;
    },
    isPhoneDisabled() {
      const raw = (this.phone || "").replace(/\D/g, "");
      return raw.length === 0;
    },

    // Family Bundle - Computed (UI hidden, keeping for potential future use)
    familyDiscountOrdersLength() {
      return this.familyDiscountOrders.length;
    },

  },  

  watch: {
    isPatientInfoInSessionStorage(newVal) {
      if (newVal) {
        this.patient = JSON.parse(sessionStorage.getItem("patient"));
      }
    },
    // Family Bundle - Watchers (UI hidden, keeping for potential future use)
    familyDiscountEligible(newVal) {
      this.updateIsShowScheduleSendDateBtn(newVal, this.familyDiscountOrdersLength);
    },
    familyDiscountOrdersLength(newVal) {
      this.updateIsShowScheduleSendDateBtn(this.familyDiscountEligible, newVal);
    },
    patientAreaCodeChangeCount(newVal) {
      if (newVal) {
        let patient = JSON.parse(sessionStorage.getItem("patient"));
        let result = formatPhoneNumber(this.phone, patient.areaCode);
        this.$store.dispatch("orderTest/order/setContactInfo", { method: "phone", info: result });
      }
    },
    defaultSettings() {
      this.getNotificationSettings(this.defaultSettings);
    },
  },  

  emits: ["changeCheckEmail", "changeCheckPhone", "updateEmail", "updatePhone"],

  mounted() {
    if (!this.isBackFromCheckoutPage) {
      this.getNotificationSettings(this.defaultSettings);
    } 
    this.updateIsShowScheduleSendDateBtn(this.familyDiscountEligible, this.familyDiscountOrdersLength);
  },

  methods: {
    getNotificationSettings(settingObj) {
      if (this.orderDraft && !_.isEmpty(this.orderDraft)) {
        return;
      }
      ["email", "text"].forEach(prop => {
        let defaultSettingProp = "send_" + prop;
        if (defaultSettingProp in settingObj) {
          let isSend = settingObj[defaultSettingProp];
          if (_.isBoolean(isSend)) {
            let notifyObj = { method: prop, isSend };
            this.$store.dispatch("orderTest/order/setSendNotification", notifyObj);
          }
        }
      });
    },
    updateIsSendNotification(method, isSend) {
      this.$store.dispatch("orderTest/order/setSendNotification", { method, isSend });
    },

    handleEditEmailOrPhone(name) {
      if (name === "email") {
        this.isEditEmail = true;
        this.prevEmail = this.emailChild;
        this.$nextTick(() => {
          this.$refs.patientEmail.focus(); // focus the input
          this.$refs.patientEmail.select(); // select text in input
        });
      }
      else {
        this.isEditPhone = true;
        this.$nextTick(() => {
          this.$refs.patientPhone.focus(); // focus the input
          this.$refs.patientPhone.select(); // select text in input
        });
      }
    },

    validateEmail(value) {
      if (value.trim()==="") {
        return false;
      }
      const regex = new RegExp("^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\\.[A-Z|a-z]{2,}$");
      if (!regex.test(value.trim())){
      let message = this.$t("OrderPage[\"Please enter correct email format.\"]");
      notify({ type: 'error', message }); 
        return false;
      }
      return true;
    },
    
    updateEmailPhone(name, info) {
      if (name=="email") {
        let valid = this.validateEmail(info);
        if (valid) {
          this.$store.dispatch("orderTest/order/setContactInfo", { method: name, info });
        }
      } else {
        let result = formatPhoneNumber(info, this.patient.areaCode);
        this.$store.dispatch("orderTest/order/setContactInfo", { method: name, info: result });
      }
    },
    // Family Bundle - Method (UI hidden, keeping for potential future use)
    updateIsShowScheduleSendDateBtn(familyDiscountEligible, familyDiscountOrdersLength) {
      if (familyDiscountEligible && familyDiscountOrdersLength > 0) {
        this.isShowScheduleSendDateBtn = false;
        // reset order send date
        this.$store.dispatch("orderTest/order/setSendDate", { date: "", time: "" });
      } else {
        this.isShowScheduleSendDateBtn = true;
      }
    }
  }  
}
</script>


<style scoped>
.send-to-patient {
  font-size: 16px;
  font-weight: bold;
  letter-spacing: 0.46px;
  line-height: 19px;
}

.send-to-patient-text {
  font-weight: 400;
  font-size: 12px;
  letter-spacing: 0.3432px;
  color: #4A4A4A;
}
.patient-contact-container {
  width: 373px;
  border-radius: 3px;
  background-color: #ffffff;
  box-shadow: 0 2px 6px 0 rgba(48, 48, 48, 0.13);
}

.email-text {
  height: 14px;
  font-size: 12px;
  letter-spacing: 0.34px;
  line-height: 14px;
}

.email-text-divider {
  box-sizing: border-box;
  height: 1px;
  border: 1px solid #e6e6e6;
}

.check-box {
  font-size: 20px;
}

.all-checked-box {
  height: 20px;
  width: 20px;
  padding-bottom: 5px;
}

.small-icon {
  font-size: 18px;
}

.schedule-date-btn {
  box-sizing: border-box;
  height: 29px;
  width: 180px;
  border: 1px solid #20A8C3;
  border-radius: 2px;
  background-color: #FFFFFF;
  color: #20A8C3;
  font-weight: 600;
  font-size: 12px;
  letter-spacing: 0.34px;
}

.calendar-icon {
  font-size: 14px;
}
</style>